import { cn } from "@veil/ui/lib/utils";
import Check from "lucide-react/dist/esm/icons/check";
import { AnimatePresence, motion, useReducedMotion } from "motion/react";
import { type ComponentType, type ReactNode, useEffect, useRef } from "react";
import { StepTransition } from "./step-transition";

export interface WizardStep {
	id: string;
	/** Outcome-based label, e.g. "Name & domains" (not "Step 2"). */
	label: string;
	/** Force the done state regardless of position (e.g. admin already exists). */
	completed?: boolean;
	/** Done but not revisitable (e.g. the one-time admin creation). */
	locked?: boolean;
}

interface WizardShellProps {
	steps: WizardStep[];
	currentIndex: number;
	/** Invoked with a completed step's index. Upcoming steps are inert. */
	onStepSelect: (index: number) => void;
	/** What this whole flow is, shown beside the mark in the rail. */
	railTitle: string;
	railIcon: ComponentType<{ className?: string }>;
	title: string;
	description: string;
	/** Short "why this step matters" blurb shown in the left rail. */
	why: string;
	/**
	 * The running summary block, rendered as-is under the "why" note. A node
	 * rather than a typed struct because the two flows summarize different
	 * things; see components/wizard/wizard-summary.tsx for the shared rows.
	 */
	summary: ReactNode;
	version?: string;
	/** Changes per step; drives the AnimatePresence swap + heading focus. */
	stepKey: string;
	/**
	 * Pinned below the step body, inside the panel column. Used by the
	 * add-worker page for Back/Continue; setup keeps its actions in each step.
	 */
	footer?: ReactNode;
	/**
	 * `fullscreen` takes over the viewport (first-run setup, which renders
	 * outside the app shell). `page` sits inside the dashboard inset instead,
	 * cancelling its padding and starting below the sticky header.
	 */
	variant?: "fullscreen" | "page";
	children: ReactNode;
}

/**
 * Enterprise split-screen shell for a multi-step flow.
 *
 *  - Left context rail (>= md): product mark, a vertical outcome-labeled
 *    stepper, the "why this matters" blurb, a running summary that fills in as
 *    steps complete, and the version pinned to the bottom.
 *  - Right panel: the active step's heading/description + form. No wrapping
 *    Card — a card inside the shell would be redundant (nested-card
 *    anti-pattern); we use spacing + a single heading block instead.
 *  - Below md: the rail collapses to a top "Step N of M" counter + dots and
 *    the panel stacks full-width.
 *
 * Focus is moved to the step heading on every transition so keyboard and
 * screen-reader users land on the new step's context.
 */
export function WizardShell({
	steps,
	currentIndex,
	onStepSelect,
	railTitle,
	railIcon: RailIcon,
	title,
	description,
	why,
	summary,
	version,
	stepKey,
	footer,
	variant = "fullscreen",
	children,
}: WizardShellProps) {
	const headingRef = useRef<HTMLHeadingElement>(null);

	// Move focus to the step heading whenever the step changes. `preventScroll`
	// keeps the split layout from jumping the rail on focus.
	useEffect(() => {
		headingRef.current?.focus({ preventScroll: true });
	}, [stepKey]);

	const page = variant === "page";
	// The dashboard inset is already the document's <main>, and there is only
	// ever one. Standalone (setup) the panel is the main landmark.
	const Panel = page ? "div" : "main";

	return (
		<div
			className={cn(
				"bg-muted/30 md:grid md:grid-cols-[clamp(20rem,26vw,24rem)_1fr]",
				// Inside the dashboard inset the shell cancels the content
				// padding sideways and upwards — the same bleed the workers
				// command bar uses — and starts below the sticky header rather
				// than claiming the whole viewport.
				page
					? "-mx-4 -mt-4 min-h-[calc(100vh-var(--header-height)-1rem)] md:-mx-6 md:-mt-6 md:min-h-[calc(100vh-var(--header-height)-1.5rem)]"
					: "min-h-screen",
			)}
		>
			{/* Desktop context rail */}
			<aside
				className={cn(
					"sticky hidden flex-col gap-8 border-r border-border/60 bg-background/60 px-7 py-8 md:flex",
					page
						? "top-(--header-height) h-[calc(100vh-var(--header-height)-1.5rem)]"
						: "top-0 h-screen",
				)}
			>
				<div className="flex items-center gap-2">
					<span className="flex size-8 items-center justify-center rounded-lg bg-primary/10 text-primary">
						<RailIcon className="size-4" />
					</span>
					<span className="font-heading text-sm font-semibold tracking-tight">
						{railTitle}
					</span>
				</div>

				<VerticalStepper
					steps={steps}
					currentIndex={currentIndex}
					onStepSelect={onStepSelect}
				/>

				<div className="rounded-lg border border-border/60 bg-muted/40 p-3">
					<p className="text-xs leading-relaxed text-muted-foreground">{why}</p>
				</div>

				{summary}

				<div className="mt-auto flex items-center justify-between pt-4 text-[0.7rem] text-muted-foreground">
					<span>VeilBrowser</span>
					{version && <span className="font-mono">v{version}</span>}
				</div>
			</aside>

			{/* Mobile top bar */}
			<div className="border-b border-border/60 bg-background/70 px-5 py-3 md:hidden">
				<div className="flex items-center justify-between">
					<div className="flex items-center gap-2">
						<RailIcon className="size-4 text-primary" />
						<span className="text-sm font-medium">
							{steps[currentIndex]?.label ?? railTitle}
						</span>
					</div>
					<span className="text-xs text-muted-foreground">
						Step {currentIndex + 1} of {steps.length}
					</span>
				</div>
				<div className="mt-2.5 flex items-center gap-1.5" aria-hidden>
					{steps.map((s, i) => {
						const done = (s.completed ?? false) || i < currentIndex;
						return (
							<span
								key={s.id}
								className={cn(
									"h-1.5 flex-1 rounded-full transition-colors",
									done && "bg-primary",
									!done && i === currentIndex && "bg-primary/60",
									!done && i > currentIndex && "bg-border",
								)}
							/>
						);
					})}
				</div>
			</div>

			{/* Right panel */}
			<Panel className="flex justify-center px-5 py-8 md:px-10 md:py-12">
				<div className="w-full max-w-xl">
					<header className="mb-6">
						<h1
							ref={headingRef}
							tabIndex={-1}
							className="font-heading text-xl font-semibold tracking-tight outline-none"
						>
							{title}
						</h1>
						<p className="mt-1.5 text-sm leading-relaxed text-muted-foreground">
							{description}
						</p>
					</header>

					<AnimatePresence mode="wait" initial={false}>
						<StepTransition key={stepKey}>{children}</StepTransition>
					</AnimatePresence>

					{/* Outside the AnimatePresence: the actions are the same actions
					    on every step, and animating them out and back in reads as
					    the buttons moving rather than the step changing. */}
					{footer ? (
						<div className="sticky bottom-0 mt-8 border-t border-border/60 bg-background/95 py-4 backdrop-blur supports-backdrop-filter:bg-background/70">
							{footer}
						</div>
					) : null}
				</div>
			</Panel>
		</div>
	);
}

function VerticalStepper({
	steps,
	currentIndex,
	onStepSelect,
}: {
	steps: WizardStep[];
	currentIndex: number;
	onStepSelect: (index: number) => void;
}) {
	const reduce = useReducedMotion();

	return (
		<ol className="flex flex-col">
			{steps.map((s, i) => {
				const done = (s.completed ?? false) || i < currentIndex;
				const active = i === currentIndex;
				// Linear: only completed steps revisitable, minus locked ones.
				const clickable = done && !active && !s.locked;
				const isLast = i === steps.length - 1;

				return (
					<li key={s.id} className="relative flex gap-3">
						{/* Connector rail between the markers */}
						{!isLast && (
							<span
								className="absolute left-3.5 top-7 h-[calc(100%-1rem)] w-px bg-border"
								aria-hidden
							>
								<motion.span
									className="block w-px origin-top bg-primary"
									initial={false}
									animate={{ scaleY: done ? 1 : 0 }}
									transition={
										reduce
											? { duration: 0 }
											: { duration: 0.3, ease: "easeOut" }
									}
									style={{ height: "100%" }}
								/>
							</span>
						)}

						<StepMarker
							done={done}
							active={active}
							index={i}
							reduce={!!reduce}
						/>

						<div className="flex-1 pb-6">
							{clickable ? (
								<button
									type="button"
									onClick={() => onStepSelect(i)}
									className="rounded-sm text-left text-sm font-medium text-foreground outline-none hover:underline focus-visible:ring-2 focus-visible:ring-ring/60"
								>
									{s.label}
								</button>
							) : (
								<span
									aria-current={active ? "step" : undefined}
									className={cn(
										"text-sm font-medium",
										active ? "text-foreground" : "text-muted-foreground",
									)}
								>
									{s.label}
								</span>
							)}
						</div>
					</li>
				);
			})}
		</ol>
	);
}

function StepMarker({
	done,
	active,
	index,
	reduce,
}: {
	done: boolean;
	active: boolean;
	index: number;
	reduce: boolean;
}) {
	return (
		<span
			className={cn(
				"z-10 flex size-7 shrink-0 items-center justify-center rounded-full border text-xs font-medium",
				done && "border-primary bg-primary text-primary-foreground",
				active &&
					"border-primary bg-background text-primary ring-2 ring-primary/20",
				!done && !active && "border-border bg-background text-muted-foreground",
			)}
			aria-hidden
		>
			{done ? (
				<motion.span
					initial={reduce ? false : { scale: 0.4, opacity: 0 }}
					animate={{ scale: 1, opacity: 1 }}
					transition={
						reduce ? { duration: 0 } : { duration: 0.2, ease: "easeOut" }
					}
					className="flex"
				>
					<Check className="size-4" />
				</motion.span>
			) : (
				index + 1
			)}
		</span>
	);
}
